
const FLAKE_COUNT = 180;
const MAX_FRAME_MS = 100;

type Flake = {
  x: number;
  y: number;
  size: number;
  fall: number;
  sway: number;
  phase: number;
  /** A disabled weather pass drains its current flakes instead of wrapping them. */
  active: boolean;
};

/** A deterministic little generator keeps screenshots and tests reproducible. */
function random(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x1_0000_0000;
  };
}

export class Snowfall {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly flakes: Flake[];
  private readonly sprite = new Image();
  private lastMs: number | null = null;
  private dpr = 1;
  private pixelRatioCap: number;
  private enabled = true;

  constructor(anchor: HTMLCanvasElement, pixelRatioCap = 2) {
    this.pixelRatioCap = clampPixelRatioCap(pixelRatioCap);
    this.canvas = document.createElement("canvas");
    this.canvas.className = "weather-snow";
    this.canvas.dataset.weather = "snow";
    this.canvas.setAttribute("aria-label", "Snowfall");
    this.ctx = this.canvas.getContext("2d", { alpha: true })!;
    this.sprite.src = "/weather/snow.png";
    const next = random(0x534e4f57); // "SNOW", not clock entropy.
    this.flakes = Array.from({ length: FLAKE_COUNT }, () => ({
      x: next(), y: next(), size: 7 + next() * 15, fall: 0.055 + next() * 0.13,
      sway: 8 + next() * 20, phase: next() * Math.PI * 2, active: true,
    }));
    anchor.parentNode?.insertBefore(this.canvas, anchor.nextSibling);
    this.resize(anchor);
  }

  resize(anchor: HTMLCanvasElement, pixelRatioCap = this.pixelRatioCap): void {
    this.pixelRatioCap = clampPixelRatioCap(pixelRatioCap);
    this.dpr = Math.min(this.pixelRatioCap, globalThis.devicePixelRatio ?? 1);
    const width = anchor.clientWidth || 960;
    const height = anchor.clientHeight || 540;
    this.canvas.width = Math.round(width * this.dpr);
    this.canvas.height = Math.round(height * this.dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
  }

  disable(): void {
    this.enabled = false;
  }

  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    for (const flake of this.flakes) {
      if (flake.active) continue;
      flake.active = true;
      flake.y = -flake.size;
    }
  }

  /** True once a disabled layer has let every already-visible flake leave. */
  get drained(): boolean {
    return !this.enabled && this.flakes.every((flake) => !flake.active);
  }

  /** Remove the finished presentation canvas. Context-loss handoff also uses
   * this immediate teardown because a frozen old canvas must never sit over
   * the fallback renderer. */
  destroy(): void {
    this.canvas.remove();
  }

  /**
   * Draw one weather frame and report whether it remains visible.  A false
   * return is the renderer's cue to remove a drained clear-weather canvas.
   */
  draw(nowMs: number): boolean {
    const previousMs = this.lastMs;
    const firstFrame = previousMs === null;
    const elapsed = firstFrame ? 16 : Math.min(MAX_FRAME_MS, Math.max(0, nowMs - previousMs));
    this.lastMs = nowMs;
    const width = this.canvas.width / this.dpr;
    const height = this.canvas.height / this.dpr;
    const ctx = this.ctx;
    // `Init` in the legacy client chooses a position around the current camera.
    // Our construction happens before a canvas has dimensions, so its 0..1 seed
    // becomes a screen coordinate on this first actual frame.
    if (firstFrame) for (const flake of this.flakes) flake.y *= height;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    for (const flake of this.flakes) {
      if (!flake.active) continue;
      flake.y += flake.fall * elapsed;
      flake.phase += elapsed * 0.002;
      if (flake.y > height + flake.size) {
        if (this.enabled) flake.y = -flake.size;
        else {
          flake.active = false;
          continue;
        }
      }
      const x = (flake.x * width + Math.sin(flake.phase) * flake.sway + width) % width;
      ctx.globalAlpha = 0.52 + (flake.size - 7) / 38;
      if (this.sprite.complete && this.sprite.naturalWidth > 0) {
        ctx.drawImage(this.sprite, x - flake.size / 2, flake.y - flake.size / 2, flake.size, flake.size);
      } else {
        // The shipped sprite may still be loading on the first frame. A white
        // disc means weather remains visible rather than blinking off.
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(x, flake.y, flake.size / 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    return !this.drained;
  }
}

function clampPixelRatioCap(cap: number): number {
  return Number.isFinite(cap) ? Math.max(1, Math.min(2, cap)) : 2;
}
